import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MEAL_TITLE,
  formatDecimal,
  formatNumber,
  formatShortDate,
  type Meal,
} from '@jungles/shared';
import { foodSelection, type SearchFood, type FoodSearchResponse } from '../lib/food-search';
import { useApi, useDebounced } from '../lib/hooks';
import { Loader, Screen } from '../components/Chrome';
import { BarcodeIcon, SearchIcon } from '../components/icons';

/** N4 · Поиск продукта — шторка на весь экран (SPEC §5). */

function Highlighted({ text, query }: { text: string; query: string }) {
  const needle = query.trim().toLowerCase();
  if (!needle) return <>{text}</>;
  const index = text.toLowerCase().indexOf(needle);
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + needle.length)}</mark>
      {text.slice(index + needle.length)}
    </>
  );
}

export function FoodSearchScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const meal = (params.get('meal') ?? 'snack') as Meal;
  const date = params.get('date') ?? '';
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 600);

  const { data: response, loading, error } = useApi<FoodSearchResponse>(
    `/api/foods?q=${encodeURIComponent(debounced.trim())}`,
  );
  const searching = loading || query !== debounced;
  const data = searching || error ? null : response;

  const openWithGrams = (food: SearchFood) => {
    if (food.external) {
      navigate(`/nutrition/food?meal=${meal}&date=${date}`, { state: { prefill: foodSelection(food).prefill, catalogSelection: true } });
      return;
    }
    navigate(`/nutrition/food?meal=${meal}&date=${date}&foodId=${food.id}`, { state: { food } });
  };

  const renderRow = (food: SearchFood, subtitle: string) => (
    <li key={food.id}>
      <button className="rn" type="button" onClick={() => openWithGrams(food)}>
        <b>
          <Highlighted text={food.name} query={debounced} />
        </b>
        <small>{subtitle}</small>
      </button>
      <button
        className="qa"
        type="button"
        onClick={() => openWithGrams(food)}
        aria-label={`Добавить «${food.name}» в ${MEAL_TITLE[meal].toLowerCase()}`}
      >
        +
      </button>
    </li>
  );

  return (
    <Screen>
      <div className="sbar" style={{ paddingTop: 18 }}>
        <label className="srch" style={{ marginTop: 0 }}>
          <SearchIcon />
          <span className="vh">Поиск продукта</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Найти продукт или блюдо"
            autoFocus
            autoComplete="off"
            maxLength={120}
          />
          {query ? (
            <button
              className="clr"
              type="button"
              onClick={() => setQuery('')}
              aria-label="Очистить"
            >
              ×
            </button>
          ) : null}
        </label>
        <button className="cnl" type="button" onClick={() => navigate(-1)}>
          Отмена
        </button>
      </div>

      <span className="ctx" style={{ marginTop: 12 }}>
        В «{MEAL_TITLE[meal]}» · {date ? formatShortDate(date) : 'сегодня'}
      </span>

      {searching ? <Loader label="Ищем…" /> : null}
      {error ? <p className="err" role="alert">{error}</p> : null}
      {data?.externalError ? <p className="err" role="alert">{data.externalError}</p> : null}
      {debounced.trim().length < 3 ? <p className="nt">Для поиска в Open Food Facts введите от 3 символов.</p> : null}

      {data && data.recent.length > 0 ? (
        <>
          <div className="sec">Мои и недавние</div>
          <ul className="res">
            {data.recent.map((food) =>
              renderRow(
                food,
                `${formatNumber(food.kcal100)} ккал / 100 г${
                  food.useCount ? ` · добавляли ${food.useCount} раз` : ''
                }`,
              ),
            )}
          </ul>
        </>
      ) : null}

      {data && data.catalog.length > 0 ? (
        <>
          <div className="sec">Каталог</div>
          <ul className="res">
            {data.catalog.map((food) =>
              renderRow(
                food,
                `на 100 г · ${formatNumber(food.kcal100)} ккал · Б ${formatDecimal(
                  food.p100,
                )} Ж ${formatDecimal(food.f100)} У ${formatDecimal(food.c100)}`,
              ),
            )}
          </ul>
        </>
      ) : null}

      {data && data.external?.length > 0 ? (
        <>
          <div className="sec">Open Food Facts</div>
          <ul className="res">
            {data.external.map((food) => renderRow(food,
              `${food.brand ? `${food.brand} · ` : ''}${formatNumber(food.kcal100)} ккал / 100 г`))}
          </ul>
        </>
      ) : null}
      <p className="nt">Источник внешнего каталога: <a href="https://world.openfoodfacts.org" target="_blank" rel="noreferrer">Open Food Facts</a> · ODbL. Сверяйте КБЖУ с упаковкой.</p>

      {data && !data.externalError && data.recent.length === 0 && data.catalog.length === 0 && !data.external?.length && !searching ? (
        <p className="empty">Ничего не нашли</p>
      ) : null}

      <div className="nf" style={{ paddingBottom: 24 }}>
        <div className="nt">Не нашли нужное?</div>
        <div className="row2">
          <button
            className="pill-btn"
            type="button"
            onClick={() => navigate(`/nutrition/food?meal=${meal}&date=${date}`)}
          >
            <img src="/assets/ui/tab-food.webp" alt="" />
            Вручную
          </button>
          <button
            className="pill-btn"
            type="button"
            onClick={() => navigate(`/nutrition/scan?meal=${meal}&date=${date}`)}
          >
            <BarcodeIcon />
            Сканировать
          </button>
        </div>
      </div>
    </Screen>
  );
}
